// src/components/Home.jsx
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import LivePythonSandbox from "./LivePythonSandbox";
import Navbar from "./navbar";

// Accent & brand colors for easy reuse
const ACCENT_BG = "bg-green-500";
const ACCENT_BORDER = "border-green-400";
const ACCENT_TEXT = "text-green-400";

const STAGES = [
  {
    id: 1,
    label: "Foundations of Computer Science",
    title: "Think Like a Computer",
    details:
      "This phase lays the intellectual groundwork for everything you'll learn later. We start by breaking down the myths about CS — it's not just for math geniuses or code wizards. You'll learn how computers really work, how data flows across the internet, and why binary logic powers everything digital.\n\nWe’ll introduce you to programming using simple concepts like variables, loops, and functions — taught through real-life analogies. You’ll also get hands-on with debugging, version control, and even build your first tiny game. By the end, you’ll not just write code — you’ll understand what happens underneath when that code runs."
  },
  {
    id: 2,
    label: "Core Coding & Data Structures",
    title: "From Hello World to Real Code",
    details:
      "In this phase, you’ll evolve from writing basic programs to architecting clean, efficient, and scalable software. We dive deep into data structures like arrays, trees, graphs, and hash maps — along with powerful algorithms for sorting, searching, and optimization.\n\nYou’ll learn how to build CRUD apps, understand MVC architecture, connect with REST APIs, and build a portfolio-worthy website. Through guided projects and real-world context, we’ll bridge theory and practical application. This is where coding gets exciting — and truly useful."
  },
  {
    id: 3,
    label: "Systems, Memory & Performance",
    title: "Under the Hood of Modern Software",
    details:
      "Once you understand how to build programs, it’s time to see how they run. We’ll demystify the core of computing — from operating systems and memory management to threads, concurrency, and compilers.\n\nYou’ll learn how your code turns into machine instructions, how memory is allocated, and why race conditions exist. We’ll also touch on networking, HTTPS, cloud basics, and software architecture — giving you the kind of deep knowledge most developers miss."
  },
  {
    id: 4,
    label: "Data Science & AI Foundations",
    title: "From Data to Intelligence",
    details:
      "CS isn’t just about writing code — it's about making sense of the world through data. This phase introduces you to data science, machine learning, neural networks, and natural language processing in the simplest, most practical way possible.\n\nEven if you’re not into math, we’ll show how Python and tools like Pandas make data analysis approachable and powerful. You’ll build projects like chatbots and recommendation engines, and understand how AI is integrated into modern apps."
  },
  {
    id: 5,
    label: "Modern Developer Workflows",
    title: "Tooling, DevOps & Clean Code",
    details:
      "Being a great developer is not just about writing logic — it’s about working smart. This module teaches you how to write clean code, use DevOps tools like GitHub Actions, understand containers (Docker), and automate deployment.\n\nYou’ll also learn advanced SQL, use real-world APIs, and build with OpenAI or cloud services. This phase sharpens your skills for production-level code and makes you team-ready."
  },
  {
    id: 6,
    label: "Career Readiness & Capstone",
    title: "Launchpad to Tech Roles",
    details:
      "Everything you’ve learned culminates in a capstone project where you solve a real-world problem — from concept to deployment. You'll also sharpen your interview skills with system design problems, mock coding rounds, and real HR prep.\n\nWe'll walk you through building a standout GitHub profile, writing effective resumes, and finding your career path — whether it's freelancing, joining a startup, or cracking FAANG interviews. You don’t just finish the course — you launch with direction, confidence, and a tangible portfolio."
  }
];

export default function Home() {
  /* timeline state */
  const [idx, setIdx]       = useState(0);           // 0-based index
  const [dir, setDir]       = useState(1);
  const [paused, setPaused] = useState(false);
  const active              = STAGES[idx];

  /* auto-rotate unless paused */
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx(i => (i + 1) % STAGES.length), 4000);
    return () => clearInterval(id);
  }, [paused]);

  const prev = () => setIdx(i => (i - 1 + STAGES.length) % STAGES.length);
  const next = () => setIdx(i => (i + 1) % STAGES.length);

  return (
    <div className="min-h-screen flex flex-col font-mono bg-[#0d1117] text-[#c9d1d9]">
      <Navbar />

      {/* ——— HERO ——— */}
      <section className="px-6 lg:px-12 py-24 text-center space-y-8">
        <h1 className="text-3xl md:text-5xl font-bold leading-snug">
          <span className="before:content-['$'] before:mr-3 before:text-green-500">
            Master Real Computer Science
          </span>
          <br className="hidden md:block" />
          <span className={ACCENT_TEXT}> with a 120-Day Guided System</span>
        </h1>
        <p className="text-base md:text-lg max-w-2xl mx-auto text-[#8b949e]">
          Not just articles. This is a crisp, concise system that teaches you core CS, step by step,
          with projects, mentorship, and real results.
        </p>
        <Link
          to="/lessons"
          className={`inline-block px-8 py-3 rounded text-lg font-semibold shadow transition transform hover:-translate-y-0.5 active:translate-y-0 ${ACCENT_BG} text-black`}
        >
          Start Learning Free →
        </Link>
      </section>

      {/* ——— TIMELINE ——— */}
      <section className="pt-12 pb-24 w-full bg-[#0d1117]">
        <h2 className={`text-xl font-bold mb-10 flex items-center justify-center gap-2 ${ACCENT_TEXT}`}>
          🧱 Your 6-Step CS Roadmap
        </h2>

        <div className="relative flex justify-center items-center">
          {/* arrows */}
          <button
            onClick={() => prev()}
            className="absolute left-2 md:left-8 p-2 text-[#8b949e] hover:text-green-400 transition"
            aria-label="previous stage"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={() => next()}
            className="absolute right-2 md:right-8 p-2 text-[#8b949e] hover:text-green-400 transition"
            aria-label="next stage"
          >
            <ChevronRight size={28} />
          </button>

          {/* animated card */}
          <div
            key={active.id}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className={`w-[96vw] sm:w-[90vw] lg:w-[80vw] xl:w-[70vw]
                        min-h-[24rem] bg-[#161b22] border border-[#30363d] rounded-xl
                        px-10 py-14 text-center shadow-green-500/20 cursor-pointer
                        transition-shadow duration-300
                        ${dir === 1 ? "animate-slide-left" : "animate-slide-right"}`}
          >
            <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full mb-6 font-semibold text-sm ${ACCENT_BG}`}>
              {active.id}
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-6">{active.label}</h3>
            <p className="text-sm md:text-base text-[#8b949e] leading-relaxed px-4 whitespace-pre-line">
              {active.details.split("\n")[0]}
            </p>
          </div>
        </div>
      </section>


      {/* ——— PROJECT SHOWCASE ——— */}
      <section className="py-24 bg-[#161b22] border-t border-[#30363d]">
        <h2 className={`text-xl font-bold mb-10 text-center ${ACCENT_TEXT}`}>
          🚢 Projects You’ll Ship
        </h2>

        <div className="overflow-x-auto snap-x snap-mandatory flex gap-8 px-6 lg:px-12 hide-scrollbar">
          {[
            {
              title: "Full-Stack Task Tracker",
              img: "/assets/tasktracker.png",
              stack: ["React", "FastAPI", "PostgreSQL", "Docker"],
              hook: "CRUD • REST • CI/CD",
            },
            {
              title: "Dijkstra Visualizer",
              img: "/assets/dijkstra.png",
              stack: ["Vanilla JS", "HTML Canvas"],
              hook: "Graphs • Path-finding",
            },
            {
              title: "HashMap Microservice",
              img: "/assets/hashmap.png",
              stack: ["Go", "Redis", "Docker"],
              hook: "Hashing • Microservices",
            },
            {
              title: "Realtime Chat & Threads",
              img: "/assets/chat.png",
              stack: ["Next.js", "WebSocket", "SQLite"],
              hook: "Concurrency • Networking",
            },
            {
              title: "Sentiment Analyzer",
              img: "/assets/sentiment.png",
              stack: ["Python", "Pandas", "Scikit-learn"],
              hook: "NLP • ML APIs",
            },
          ].map((p) => (
            <article
              key={p.title}
              className="snap-center shrink-0 w-96 md:w-[26rem] bg-[#0d1117] border border-[#30363d] rounded-xl shadow-lg overflow-hidden"
            >
              <img
                src={p.img}
                alt={p.title}
                className="h-56 w-full object-cover"
                loading="lazy"
              />
              <div className="p-6 flex flex-col gap-4">
                <header>
                  <h3 className="font-semibold text-lg">{p.title}</h3>
                  <p className="text-xs text-[#8b949e]">{p.hook}</p>
                </header>

                <div className="flex flex-wrap gap-2 text-xs">
                  {p.stack.map((s) => (
                    <span
                      key={s}
                      className="px-2 py-0.5 bg-[#21262d] rounded-full whitespace-nowrap"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>


      {/* ——— TESTIMONIALS ——— */}
      <section className="py-24 bg-[#0d1117] px-6 lg:px-12">
        <h2 className={`text-xl font-bold mb-12 flex items-center justify-center gap-2 ${ACCENT_TEXT}`}>
          🌟 What Learners Say
        </h2>

        <div className="mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-3 gap-10 place-items-center">
          {[
            { quote: "Kodanza turned scary CS theory into aha-moments. I cracked my first interview in 5 weeks!", name: "Ritika • SDE @ Razorpay", avatar: "https://i.pravatar.cc/96?img=5" },
            { quote: "The interactive code blocks are genius. Learned Python generators on my phone during commute.", name: "Arun • Backend @ Groww", avatar: "https://i.pravatar.cc/96?img=12" },
            { quote: "From zero to deploying an API with Docker in three months — best decision I made.", name: "Sonal • Freelancer", avatar: "https://i.pravatar.cc/96?img=32" },
          ].map((t, i) => (
            <figure key={i} className="w-80 bg-[#161b22] border border-[#30363d] rounded-xl p-8 flex flex-col gap-6 shadow-lg">
              <blockquote className="text-sm leading-relaxed grow">“{t.quote}”</blockquote>
              <figcaption className="flex flex-col items-center gap-3 text-sm text-[#8b949e]">
                <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full border border-[#30363d]" loading="lazy" />
                {t.name}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ——— FOOTER ——— */}
      <footer className="mt-auto text-center py-10 text-xs text-[#8b949e] bg-[#0d1117] border-t border-[#30363d]">
        © {new Date().getFullYear()} Kodanza. All rights reserved.
      </footer>
    </div>
  );
}